import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { backend, useApp } from '../data/store';
import { Icon } from '../ui/icons';
import { CodeInput } from '../ui/CodeInput';
import { REGISTER_URL } from './Login';

/**
 * Signed in, but this account has no masjid yet: it registered and is waiting for its code. Entering the 5-digit
 * code from Noor MS opens the masjid.
 */
export function Onboarding() {
  const session = useApp((s) => s.session);
  const nav = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const redeem = async (c: string) => {
    if (c.length !== 5 || busy) return;
    setBusy(true); setError('');
    try {
      const s = await backend.redeemCode!(c);
      useApp.setState({ session: s });
      await useApp.getState().reload();
    } catch (x) { setError(x instanceof Error ? x.message : 'That code didn’t work.'); setCode(''); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    if (!session?.email) return;
    setBusy(true); setError('');
    try { setNote(await backend.resendCode!(session.email)); } catch (x) { setError(x instanceof Error ? x.message : 'Couldn’t send a new code.'); }
    finally { setBusy(false); }
  };
  const signOut = async () => { await backend.signOut(); useApp.getState().setSession(null); nav('/login'); };

  return (
    <form className="card glass settings-card" style={{ margin: '40px auto', width: 'min(520px, 100%)', textAlign: 'center' }} onSubmit={(e) => { e.preventDefault(); void redeem(code); }}>
      <div className="card-head" style={{ justifyContent: 'center' }}><Icon name="sparkles" /><h2>Enter Masjid Code</h2></div>
      <p className="hint" style={{ margin: 0 }}>
        {backend.redeemCode ? 'Your masjid is registered. Enter the 5-digit code Noor MS sent you to open your dashboard.' : 'This account isn’t part of a masjid yet. Ask your masjid’s admin to invite you.'}
      </p>
      {backend.redeemCode && <>
        <CodeInput value={code} onChange={(v) => { setCode(v); if (error) setError(''); }} onComplete={(v) => void redeem(v)} disabled={busy} bad={!!error} autoFocus />
        {error && <div className="banner bad" role="alert">{error}</div>}
        {note && <div className="banner info">{note}</div>}
        <button className="btn primary lg" disabled={busy || code.length !== 5}>{busy ? 'Checking…' : 'Continue'}</button>
        <div className="login-links">
          <button type="button" className="link" onClick={() => void resend()} disabled={busy}>Resend Code to {session?.email}</button>
          <a className="link" href={REGISTER_URL} target="_blank" rel="noopener">Register a masjid</a>
        </div>
      </>}
      <button type="button" className="btn plain" onClick={() => void signOut()}><Icon name="logout" size={18} />Sign out</button>
    </form>
  );
}

/**
 * Signed in with a NoorClass account (noorclass.noormsystems.co.uk shares NoorView's sign-ins) that has no masjid
 * in NoorView. The dashboard behind stays locked; the only ways on are buying a plan, a masjid code from Noor MS
 * (for a NoorClass teacher whose masjid has since bought NoorView), or signing out.
 */
export function NoAccess() {
  const session = useApp((s) => s.session);
  const nav = useNavigate();
  const [code, setCode] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => { if (!code) card.current?.focus(); }, [code]);
  const signOut = async () => { await backend.signOut(); useApp.getState().setSession(null); nav('/login'); };
  return (
    <div className="scrim" role="presentation">
      {code ? <Onboarding /> : (
        <div ref={card} tabIndex={-1} className="card glass settings-card noaccess" role="alertdialog" aria-modal="true" aria-labelledby="na-title" aria-describedby="na-text" style={{ width: 'min(460px, 100%)', textAlign: 'center', outline: 'none' }}>
          <div className="card-head" style={{ justifyContent: 'center' }}><Icon name="lock" /><h2 id="na-title">No access to NoorView</h2></div>
          <p id="na-text" className="hint" style={{ margin: 0 }}>
            {session?.email ? <><b>{session.email}</b> is a NoorClass account</> : 'This is a NoorClass account'}, so it doesn’t include NoorView. To use NoorView for your masjid, please purchase a plan.
          </p>
          <a className="btn primary lg" href={REGISTER_URL} target="_blank" rel="noopener">Purchase a plan</a>
          <div className="login-links"><button type="button" className="link" onClick={() => setCode(true)}>I have a masjid code</button></div>
          <button type="button" className="btn plain" onClick={() => void signOut()}><Icon name="logout" size={18} />Sign out</button>
        </div>
      )}
    </div>
  );
}
